// src/pages/Register.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  HiEye,
  HiEyeOff,
  HiPhone,
  HiLockClosed,
  HiUser,
  HiMail,
  HiKey,
} from "react-icons/hi";
import { RiInformation2Fill } from "react-icons/ri";
import {
  useAddUserMutation,
  useSendRegisterOtpMutation,
} from "../toolkitServices/userApi";
import { setUser } from "../slices/authSlice";
import AppLoader from "../components/AppLoader";
import toast from "react-hot-toast";

const getErrMsg = (err) =>
  err?.data?.message ||
  err?.data?.msg ||
  err?.error ||
  err?.message ||
  "Registration failed";

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpTargetPhone, setOtpTargetPhone] = useState("");
  const [otpTargetEmail, setOtpTargetEmail] = useState("");

  const {
    register,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useForm();

  const [createUser, { isLoading: isCreating }] = useAddUserMutation();
  const [sendRegisterOtp, { isLoading: isSendingOtp }] =
    useSendRegisterOtpMutation();

  useEffect(() => {
    if (otpTimer <= 0) return;

    const timer = setTimeout(() => {
      setOtpTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [otpTimer]);

  const handleSendOtp = async () => {
    const isValid = await trigger(["name", "email", "phone", "password"]);

    if (!isValid) {
      toast.error("Please fill all details correctly first.");
      return;
    }

    const values = getValues();

    try {
      await sendRegisterOtp({
        email: values.email,
        phone: values.phone,
      }).unwrap();

      setOtpSent(true);
      setOtpTimer(60);
      setOtpTargetPhone(values.phone);
      setOtpTargetEmail(values.email);

      toast.success("OTP sent successfully on your mobile number.");
    } catch (err) {
      const msg = getErrMsg(err);
      toast.error(msg);
      console.log(err);
    }
  };

  const onSubmit = async (formData) => {
    if (!otpSent) {
      await handleSendOtp();
      return;
    }

    const currentEmail = String(formData.email || "").toLowerCase().trim();
    const targetEmail = String(otpTargetEmail || "").toLowerCase().trim();

    if (formData.phone !== otpTargetPhone || currentEmail !== targetEmail) {
      toast.error("Mobile number or email changed. Please send OTP again.");
      setOtpSent(false);
      setOtpTimer(0);
      return;
    }

    try {
      const res = await createUser({
        userName: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        otp: formData.otp,
      }).unwrap();

      dispatch(setUser(res?.data));

      toast.success(
        "Welcome to SwitchIn, your account has been created successfully."
      );

      navigate("/dashboard");
    } catch (err) {
      const msg = getErrMsg(err);
      toast.error(msg);
      console.log(err);
    }
  };

  if (isCreating) return <AppLoader />;

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-x-hidden bg-gradient-to-br from-[#f5f3ff] via-[#fefce8] to-[#e0f2fe] px-3 py-4 sm:px-4 md:h-dvh md:overflow-hidden md:py-3">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(196,181,253,0.28),transparent_55%),radial-gradient(circle_at_bottom,_rgba(251,191,36,0.18),transparent_55%)]" />
        <div className="absolute -top-24 left-6 h-56 w-56 rounded-full bg-[#c4b5fd]/40 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="flex w-full max-w-4xl overflow-hidden rounded-3xl bg-white/90 shadow-xl shadow-violet-200/70 ring-1 ring-violet-100 backdrop-blur-md md:h-[min(560px,calc(100dvh-24px))]"
      >
        <div className="flex w-full flex-col md:flex-row">
          <div className="flex w-full flex-col border-slate-100 px-5 py-5 sm:px-7 md:w-1/2 md:border-r md:px-8 md:py-5">
            <div className="shrink-0">
              <Link
                to="/"
                className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-600"
              >
                SwitchIn
              </Link>

              <h2 className="mt-1 text-lg font-semibold text-slate-900 sm:text-xl md:text-2xl">
                Create your account
              </h2>

              <p className="mt-1.5 text-[11px] leading-5 text-slate-500 sm:text-xs">
                Set up your login to start managing your jewellery shop.
              </p>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-3 flex min-h-0 flex-1 flex-col"
            >
              <div className="space-y-2 md:space-y-1.5">
                {/* Name */}
                <div>
                  <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                    Name
                  </label>

                  <div
                    className={`group mt-1 flex h-[40px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                      errors.name ? "border-red-400" : "border-slate-200"
                    }`}
                  >
                    <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                        <HiUser className="text-sm" />
                      </span>
                    </div>

                    <input
                      {...register("name", {
                        required: "Name is required",
                      })}
                      className="h-full w-full bg-transparent px-3 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                      type="text"
                      placeholder="Your full name"
                    />
                  </div>

                  {errors.name && (
                    <p className="mt-1 text-[11px] text-red-500">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                    Email
                  </label>

                  <div
                    className={`group mt-1 flex h-[40px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                      errors.email ? "border-red-400" : "border-slate-200"
                    }`}
                  >
                    <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                        <HiMail className="text-sm" />
                      </span>
                    </div>

                    <input
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Invalid email format",
                        },
                      })}
                      className="h-full w-full bg-transparent px-3 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                      type="email"
                      placeholder="you@store.com"
                    />
                  </div>

                  {errors.email && (
                    <p className="mt-1 text-[11px] text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="group relative flex w-fit items-center gap-1 text-[11px] font-medium text-slate-700 md:text-xs">
                    Mobile Number
                    <RiInformation2Fill className="mt-[1px] cursor-pointer text-gray-500" />

                    <span className="absolute left-0 top-full z-10 mt-1 hidden whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] text-white shadow-lg group-hover:block">
                      Use an active mobile number. <br />
                      This number will help recover your account.
                    </span>
                  </label>

                  <div
                    className={`group mt-1 flex h-[40px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                      errors.phone ? "border-red-400" : "border-slate-200"
                    }`}
                  >
                    <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                        <HiPhone className="text-sm" />
                      </span>
                    </div>

                    <div className="flex h-full items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-violet-700">
                      +91
                    </div>

                    <input
                      {...register("phone", {
                        required: "Mobile number is required",
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message: "Enter a valid 10 digit mobile number",
                        },
                      })}
                      className="h-full w-full bg-transparent px-3 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="9876543210"
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                      }}
                    />
                  </div>

                  {errors.phone && (
                    <p className="mt-1 text-[11px] text-red-500">
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                {/* OTP */}
                {otpSent && (
                  <div>
                    <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                      Enter OTP
                    </label>

                    <div
                      className={`group mt-1 flex h-[40px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                        errors.otp ? "border-red-400" : "border-slate-200"
                      }`}
                    >
                      <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                          <HiKey className="text-sm" />
                        </span>
                      </div>

                      <input
                        {...register("otp", {
                          required: "OTP is required",
                          pattern: {
                            value: /^[0-9]{6}$/,
                            message: "Enter a valid 6 digit OTP",
                          },
                        })}
                        className="h-full w-full bg-transparent px-3 text-sm font-semibold tracking-[0.35em] text-slate-900 outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400"
                        type="tel"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter OTP"
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6);
                        }}
                      />
                    </div>

                    {errors.otp && (
                      <p className="mt-1 text-[11px] text-red-500">
                        {errors.otp.message}
                      </p>
                    )}

                    <div className="mt-1.5 flex items-center justify-between gap-3 rounded-xl bg-violet-50 px-3 py-1.5">
                      <p className="text-[10px] font-medium text-slate-600">
                        OTP sent to{" "}
                        <span className="font-semibold text-violet-700">
                          +91 {otpTargetPhone}
                        </span>
                      </p>

                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp || otpTimer > 0}
                        className="shrink-0 text-[10px] font-semibold text-violet-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                      >
                        {isSendingOtp
                          ? "Sending..."
                          : otpTimer > 0
                          ? `Resend in ${otpTimer}s`
                          : "Resend OTP"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Password */}
                <div>
                  <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                    Password
                  </label>

                  <div
                    className={`group relative mt-1 flex h-[40px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                      errors.password ? "border-red-400" : "border-slate-200"
                    }`}
                  >
                    <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                        <HiLockClosed className="text-sm" />
                      </span>
                    </div>

                    <input
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Minimum 6 characters required",
                        },
                      })}
                      className="h-full w-full bg-transparent px-3 pr-11 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a secure password"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-violet-50 hover:text-violet-600"
                    >
                      {showPassword ? <HiEyeOff /> : <HiEye />}
                    </button>
                  </div>

                  {errors.password && (
                    <p className="mt-1 text-[11px] text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 shrink-0">
                <button
                  disabled={isCreating || isSendingOtp}
                  type={otpSent ? "submit" : "button"}
                  onClick={!otpSent ? handleSendOtp : undefined}
                  className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-400/60 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-80"
                >
                  {isCreating
                    ? "Creating account…"
                    : isSendingOtp
                    ? "Sending OTP..."
                    : otpSent
                    ? "Create account"
                    : "Send OTP"}
                </button>

                <p className="mt-3 text-center text-[11px] text-slate-500">
                  Already registered?{" "}
                  <Link
                    to="/login"
                    className="font-medium text-violet-600 hover:underline"
                  >
                    Log in
                  </Link>
                </p>
              </div>
            </form>
          </div>

          <div className="relative hidden bg-gradient-to-br from-[#312e81] via-[#4c1d95] to-[#f97316] text-white md:flex md:w-1/2">
            <div className="flex flex-1 items-center justify-center px-6 py-6">
              <div className="relative w-full max-w-sm">
                <div className="absolute -inset-6 rounded-[32px] bg-white/15 blur-3xl" />

                <div className="relative flex items-center justify-center overflow-hidden rounded-[28px] bg-slate-900/30 shadow-2xl shadow-black/40 ring-1 ring-white/25 backdrop-blur-sm">
                  <img
                    src="/welcome.svg"
                    alt="Welcome to SwitchIn"
                    className="h-60 w-full object-contain p-5 lg:h-64"
                  />
                </div>
              </div>
            </div>

            <p className="absolute bottom-5 right-6 text-[10px] text-white/75">
              Create your SwitchIn workspace in minutes
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;